import { Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { dsDtos, dsEnums } from '@vectronic/openApi-admin';
import { ConfirmationDialogComponent } from '@vectronic/ui';
import { DialogService, FilterItemWithLabel, SnackbarService } from '@vectronic/util';
import { combineLatest } from 'rxjs';
import { CamerasFormatService } from '../../services/commands/cameras-format.service';
import { ScheduleTemplatesProviderService } from '../../services/schedule-templates-provider.service';
import { ScheduleServices } from '../../services/schedule.service';
import { ScheduleViewComponent } from '../schedule-view/schedule-view.component';

@Component({
  selector: 'rc-edit-schedule-template',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIcon,
    MatInputModule,
    MatSelectModule,
    ScheduleViewComponent,
  ],
  templateUrl: './edit-schedule-template.component.html',
  styleUrls: ['./edit-schedule-template.component.scss'],
})
export class EditScheduleTemplateComponent {
  private readonly adminUserAccountId = -1;
  private scheduleServices = inject(ScheduleServices);
  private dialogService = inject(DialogService);
  private snackbarService = inject(SnackbarService);
  private scheduleTemplatesProvider = inject(ScheduleTemplatesProviderService);
  private camerasFormatService = inject(CamerasFormatService);
  private destroyRef = inject(DestroyRef);

  scheduleTemplate = input<dsDtos.ScheduleTemplateAdmin>();
  hideCustomerVisibility = input<boolean>(false);
  hideUserId = input<boolean>(false);
  buttonLabel = input<string>('Save Changes');
  buttonIcon = input<string>('save');
  readonly = input<boolean>();
  hasHighActivityCameraTrigger = input<boolean>(false);

  save = output<dsDtos.ScheduleTemplateAdmin>();

  templateName = signal('');
  selectedScheduleType = signal<dsEnums.ScheduleType | undefined>(undefined);
  visibleForCustomers = signal(false);
  selectedUserAccountId = signal<number>(-1);
  ownerControl = new FormControl<string | FilterItemWithLabel<number>>('', {
    nonNullable: true,
  });
  ownerSearch = signal('');
  scheduleTypes: dsEnums.ScheduleType[] = ['GPS', 'VHF', 'COMMUNICATION', 'CAMERA'];
  availableUserAccounts = computed(() => this.scheduleTemplatesProvider.userAccounts());
  filteredUserAccounts = computed(() => {
    const search = this.ownerSearch().trim().toLowerCase();

    if (!search) {
      return this.availableUserAccounts();
    }

    return this.availableUserAccounts().filter((userAccount) => {
      const label = this.getUserAccountLabel(userAccount).toLowerCase();
      const additionalInfo = userAccount.additionalInfo?.toLowerCase() ?? '';
      return label.includes(search) || additionalInfo.includes(search);
    });
  });

  isScheduleFilled = computed(() => {
    const type = this.selectedScheduleType();
    if (!type) {
      return false;
    }
    const hasInitialSchedule = this.scheduleTemplate()?.schedule?.scheduleType === type;
    return hasInitialSchedule || this.scheduleServices[type].isFilledAndChanged();
  });

  hasTemplatesForSelectedScheduleType = computed(() => {
    const type = this.selectedScheduleType();
    if (!type) {
      return false;
    }

    return (
      this.scheduleTemplatesProvider
        .templates()
        ?.some((template) => template.schedule?.scheduleType === type) ?? false
    );
  });

  videoFormatsOptions = toSignal(this.camerasFormatService.videoFormatsFromDeviceType$, {
    initialValue: [],
  });
  photoFormatsOptions = toSignal(this.camerasFormatService.photoFormatsFromDeviceType$, {
    initialValue: [],
  });

  constructor() {
    toObservable(this.scheduleTemplate)
      .pipe(takeUntilDestroyed())
      .subscribe((template) => {
        if (template) {
          this.templateName.set(template.templateName ?? '');
          this.selectedScheduleType.set(template.schedule?.scheduleType);
          this.visibleForCustomers.set(template.visibleForCustomers);
          this.selectedUserAccountId.set(template.userAccountId ?? -1);
          const selectedUserAccount = this.availableUserAccounts().find(
            (userAccount) => userAccount.item === (template.userAccountId ?? -1),
          );
          this.ownerControl.setValue(selectedUserAccount ?? '', { emitEvent: false });
          this.ownerSearch.set('');
        }
      });

    this.ownerControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      this.ownerSearch.set(typeof value === 'string' ? value : this.getUserAccountLabel(value));
    });

    combineLatest([toObservable(this.scheduleTemplate), toObservable(this.selectedScheduleType)])
      .pipe(takeUntilDestroyed())
      .subscribe(([template, selectedType]) => {
        if (selectedType) {
          const schedules: dsDtos.Schedule[] = [];
          if (template && selectedType === template.schedule?.scheduleType) {
            if (selectedType === 'CAMERA') {
              schedules.push({
                scheduleType: 'CAMERA',
                cameraScheduleRules: template.schedule?.cameraScheduleRules,
                rules: undefined,
              } as dsDtos.Schedule);
            } else {
              schedules.push({
                scheduleType: selectedType,
                rules: template.schedule?.rules,
                cameraScheduleRules: undefined,
              } as dsDtos.Schedule);
            }
          }
          this.scheduleServices.initializeSchedules(schedules, false);
        }
      });
  }

  getLabel(type: dsEnums.ScheduleType): string {
    switch (type) {
      case 'GPS':
        return 'GPS';
      case 'VHF':
        return 'Beacon';
      case 'COMMUNICATION':
        return 'Communication';
      case 'CAMERA':
        return 'Camera';
      default:
        return type;
    }
  }

  onDefaultTemplateChange(value: boolean) {
    if (value) {
      this.dialogService.open(ConfirmationDialogComponent, {
        data: {
          title: 'Set as Visible For Customers',
          message:
            'Are you sure you want to set this template as visible for customers? This affects all customers.',
        },
        afterConfirm: () => {
          this.visibleForCustomers.set(true);
        },
        afterCancel: () => {
          this.visibleForCustomers.set(false);
        },
      });
    } else {
      this.visibleForCustomers.set(false);
    }
  }

  onUserAccountChange(userAccountId: number) {
    this.selectedUserAccountId.set(userAccountId);

    if (userAccountId !== this.adminUserAccountId) {
      this.visibleForCustomers.set(true);
    }
  }

  onUserAccountSelected(userAccount: FilterItemWithLabel<number>) {
    this.onUserAccountChange(userAccount.item);
  }

  clearOwnerInput() {
    this.ownerControl.setValue('');
  }

  userAccountDisplayFn(userAccount: string | FilterItemWithLabel<number> | null) {
    if (!userAccount) return '';
    return typeof userAccount === 'string' ? userAccount : this.getUserAccountLabel(userAccount);
  }
  //tOD
  onSave() {
    const template = this.scheduleTemplate();
    const type = this.selectedScheduleType();
    const result: dsDtos.ScheduleTemplateAdmin = {
      ...template,
      templateName: this.templateName(),
      userAccountId: this.selectedUserAccountId(),
      visibleForCustomers: this.visibleForCustomers(),
    };
    if (type) {
      const isCamera = type === 'CAMERA';
      const currentRules = Object.values(this.scheduleServices[type].rules$.value).filter(Boolean);

      result.schedule = {
        scheduleType: type,
        userDefined: true,
        rules: isCamera ? undefined : currentRules,
        cameraScheduleRules: isCamera ? currentRules : undefined,
      };
    }

    this.save.emit(result);
  }

  getUserAccountLabel(userAccount: FilterItemWithLabel<number>): string {
    if (userAccount.item === this.adminUserAccountId) {
      return 'Admin';
    }

    return userAccount.additionalInfo
      ? `${userAccount.additionalInfo} (${userAccount.label})`
      : userAccount.label;
  }
}
